import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { inr } from "@/lib/format";
import { Skeleton } from "@/components/Shared";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { CheckCircle2, AlertTriangle, XCircle, KeyRound, ExternalLink, RefreshCw } from "lucide-react";
import { SyncMonitor } from "@/components/SyncMonitor";

const AmazonLiveOrders = () => {
  const [data, setData] = useState(null);
  const [status, setStatus] = useState(null);
  const [syncing, setSyncing] = useState(false);

  const load = () => {
    api.get("/amazon/orders?limit=5").then(({ data }) => setData(data));
    api.get("/amazon/sync/status").then(({ data }) => {
      setStatus(data);
      setSyncing(!!data.running);
    });
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (!syncing) return;
    const timer = setInterval(async () => {
      const { data: st } = await api.get("/amazon/sync/status");
      setStatus(st);
      if (!st.running) {
        clearInterval(timer);
        setSyncing(false);
        if (st.error) toast.error(`Sync failed: ${st.error}`);
        else toast.success(`Sync complete — ${st.counts?.orders?.toLocaleString("en-IN")} orders in database`);
        api.get("/amazon/orders?limit=5").then(({ data }) => setData(data));
      }
    }, 4000);
    return () => clearInterval(timer);
  }, [syncing]);

  const sync = async () => {
    await api.post("/amazon/sync");
    setSyncing(true);
  };

  return (
    <section className="bg-card border border-border rounded-lg p-6 space-y-4 shadow-sm" data-testid="amazon-live-orders">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="font-heading text-base font-semibold tracking-tight">Live Amazon.in data</h3>
          <p className="text-xs text-muted-foreground mt-0.5" data-testid="sync-status-text">
            {syncing
              ? `Syncing… ${status?.phase || ""}`
              : status?.last_sync
                ? `Last sync: ${new Date(status.last_sync).toLocaleString("en-IN")} · auto-syncs daily · 6-month retention`
                : "Auto-sync runs daily. First full sync (180 days) starts automatically."}
          </p>
          {status?.counts && (
            <p className="text-xs text-muted-foreground mt-1">
              {status.counts.orders?.toLocaleString("en-IN")} orders · {status.counts.order_items?.toLocaleString("en-IN")} order items · {status.counts.finance_events?.toLocaleString("en-IN")} finance events · {status.counts.settlement_groups} settlements · {status.counts.inventory_skus} FBA SKUs
            </p>
          )}
        </div>
        <Button size="sm" onClick={sync} disabled={syncing} className="rounded-full" data-testid="amazon-sync-button">
          <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${syncing ? "animate-spin" : ""}`} aria-hidden="true" />
          {syncing ? "Syncing…" : "Sync now"}
        </Button>
      </div>
      {data?.orders?.length > 0 && (
        <table className="w-full text-sm">
          <thead className="text-xs uppercase tracking-wider text-muted-foreground">
            <tr>{["Order ID", "Date", "Status", "Fulfillment", "Total"].map((h) => <th key={h} className="text-left px-2 py-2 font-semibold">{h}</th>)}</tr>
          </thead>
          <tbody>
            {data.orders.map((o) => (
              <tr key={o.order_id} className="border-t border-border" data-testid={`amazon-order-${o.order_id}`}>
                <td className="px-2 py-2 font-mono text-xs">{o.order_id}</td>
                <td className="px-2 py-2 text-muted-foreground text-xs">{o.purchase_date?.slice(0, 10)}</td>
                <td className="px-2 py-2"><Badge className="border-0 bg-muted text-muted-foreground font-semibold">{o.status}</Badge></td>
                <td className="px-2 py-2 text-xs">{o.fulfillment === "AFN" ? "FBA" : "Self-ship"}</td>
                <td className="px-2 py-2 font-semibold">{inr(o.total)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
};

const STATUS = {
  connected: { label: "Connected", cls: "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400", Icon: CheckCircle2, iconCls: "text-green-600" },
  credentials_valid: { label: "Credentials Verified", cls: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-400", Icon: AlertTriangle, iconCls: "text-amber-600" },
  pending_approval: { label: "Pending Flipkart Approval", cls: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-400", Icon: AlertTriangle, iconCls: "text-amber-600" },
  not_configured: { label: "Not Configured", cls: "bg-muted text-muted-foreground", Icon: XCircle, iconCls: "text-muted-foreground" },
  error: { label: "Error", cls: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400", Icon: XCircle, iconCls: "text-red-600" },
};

export default function Integrations() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/integrations/status").then(({ data }) => setData(data));
  }, []);

  if (!data) return <div className="space-y-4 max-w-3xl"><Skeleton className="h-24" /><Skeleton className="h-24" /><Skeleton className="h-24" /></div>;

  return (
    <div className="max-w-3xl space-y-8" data-testid="integrations-page">
      <p className="text-sm text-muted-foreground">
        Live status of your channel API connections. Dashboard runs on <strong>live Amazon.in data</strong>, auto-synced daily with 6-month retention. Flipkart and Website join as they connect.
      </p>
      <div className="space-y-3">
        {data.integrations.map((i) => {
          const s = STATUS[i.status] || STATUS.error;
          return (
            <div key={i.channel} className="bg-card border border-border rounded-lg p-5 flex items-start gap-4 shadow-sm" data-testid={`integration-${i.channel.split(" ")[0].toLowerCase()}`}>
              <s.Icon className={`h-5 w-5 mt-0.5 shrink-0 ${s.iconCls}`} aria-hidden="true" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 flex-wrap">
                  <p className="font-heading font-semibold">{i.channel}</p>
                  <Badge className={`${s.cls} border-0 font-semibold`}>{s.label}</Badge>
                </div>
                <p className="text-sm text-muted-foreground mt-1.5">{i.message}</p>
              </div>
            </div>
          );
        })}
        <div className="bg-card border border-border rounded-lg p-5 flex items-start gap-4 shadow-sm" data-testid="integration-claude">
          <KeyRound className="h-5 w-5 mt-0.5 shrink-0 text-blue-600" aria-hidden="true" />
          <div>
            <div className="flex items-center gap-3">
              <p className="font-heading font-semibold">Claude AI</p>
              <Badge className="bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400 border-0 font-semibold">
                {data.llm_key_in_use === "own_anthropic_key" ? "Your Anthropic Key" : "Emergent Universal Key"}
              </Badge>
            </div>
            <p className="text-sm text-muted-foreground mt-1.5">
              {data.llm_key_in_use === "own_anthropic_key"
                ? "AI Assistant and queries are billed to your own Anthropic account (akshay-api-key)."
                : "Using the Emergent Universal Key until your Anthropic key is added."}
            </p>
          </div>
        </div>
      </div>

      {data.integrations[0]?.status === "connected" && (
        <>
          <SyncMonitor />
          <AmazonLiveOrders />
        </>
      )}

      {data.integrations[0]?.status !== "connected" && (
      <section className="bg-muted/60 border border-border rounded-lg p-6 space-y-4" data-testid="amazon-authorize-guide">
        <h3 className="font-heading text-base font-semibold tracking-tight">Get your Amazon refresh token (one-time, ~2 minutes)</h3>
        <p className="text-sm text-muted-foreground">Your LWA credentials for <span className="font-mono text-xs">{data.amazon_app_id}</span> are verified. To pull real orders, settlements and fees, Amazon needs you to self-authorize the app:</p>
        <ol className="space-y-2.5 text-sm list-decimal pl-5">
          <li>Open <span className="font-semibold">Developer Central</span> → solutionproviderportal.amazon.com <ExternalLink className="inline h-3 w-3" aria-hidden="true" /></li>
          <li>On <span className="font-mono text-xs">SND-CLAUDE-CONNECT</span>, click the <span className="font-semibold">▼ arrow next to "Edit App"</span> → <span className="font-semibold">Authorize</span></li>
          <li>Click <span className="font-semibold">"Generate refresh token"</span> (for the marketplaces you sell in)</li>
          <li>Copy the token (starts with <span className="font-mono text-xs">Atzr|...</span>) and paste it here in chat — I'll plug it in and switch Amazon to live data</li>
        </ol>
        <p className="text-xs text-muted-foreground">Note: the app is currently in Draft status — self-authorization works for your own seller account even in Draft.</p>
      </section>
      )}
    </div>
  );
}
